import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Plus,
  Save,
  Send,
  Upload,
  UserPlus,
  Pill,
  Stethoscope,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { farmDelete, farmGet, farmPost, farmPut } from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const emptyCliente = {
  nome: "",
  cognome: "",
  codice_fiscale: "",
  telefono: "",
  email: "",
  medico_id: "",
  note: "",
};

export default function ClientiDiretti() {
  const { token } = useFarmaciaAuth();
  const [clienti, setClienti] = useState([]);
  const [medici, setMedici] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyCliente);
  const [terapie, setTerapie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [medicoForm, setMedicoForm] = useState({ nome: "", email: "", telefono: "" });
  const [showMedici, setShowMedici] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        farmGet("/farmacia/clienti", token),
        farmGet("/farmacia/medici", token),
      ]);
      setClienti(c.items || []);
      setMedici(m.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openCliente = async (id) => {
    const c = await farmGet(`/farmacia/clienti/${id}`, token);
    setSelected(c);
    setForm({
      nome: c.nome || "",
      cognome: c.cognome || "",
      codice_fiscale: c.codice_fiscale || "",
      telefono: c.telefono || "",
      email: c.email || "",
      medico_id: c.medico_id || "",
      note: c.note || "",
    });
    setTerapie(c.terapie || []);
  };

  const nuovo = () => {
    setSelected(null);
    setForm(emptyCliente);
    setTerapie([]);
  };

  const saveCliente = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error("Nome e cognome obbligatori");
      return;
    }
    setSaving(true);
    try {
      if (selected?.id) {
        await farmPut(`/farmacia/clienti/${selected.id}`, token, form);
        await farmPut(`/farmacia/clienti/${selected.id}/terapie`, token, { terapie });
        toast.success("Cliente aggiornato");
        await openCliente(selected.id);
      } else {
        const created = await farmPost("/farmacia/clienti", token, form);
        if (terapie.length) {
          await farmPut(`/farmacia/clienti/${created.id}/terapie`, token, { terapie });
        }
        toast.success("Cliente creato");
        await load();
        await openCliente(created.id);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const inviaRichiesta = async (file) => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      await farmPost(`/farmacia/clienti/${selected.id}/richieste`, token, fd, true);
      toast.success(file ? "Richiesta con PDF inviata" : "Richiesta terapie generata e pronta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creazione richiesta fallita");
    } finally {
      setSaving(false);
    }
  };

  const uploadRicetta = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected?.id) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("cliente_id", selected.id);
      await farmPost("/farmacia/ricette/ricette-medico", token, fd, true);
      toast.success("Ricetta caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload ricetta fallito");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const addTerapia = () => {
    setTerapie((t) => [
      ...t,
      { id: crypto.randomUUID?.() || String(Date.now()), farmaco: "", dosaggio: "", frequenza: "", attiva: true },
    ]);
  };

  const saveMedico = async () => {
    if (!medicoForm.nome.trim()) return;
    try {
      await farmPost("/farmacia/medici", token, medicoForm);
      toast.success("Medico aggiunto");
      setMedicoForm({ nome: "", email: "", telefono: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FarmaciaPageChrome>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setShowMedici((v) => !v)}>
          <Stethoscope className="mr-2 h-4 w-4" />
          Medici
        </Button>
        <Button size="sm" className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]" onClick={nuovo}>
          <UserPlus className="mr-2 h-4 w-4" />
          Nuovo cliente
        </Button>
      </FarmaciaPageChrome>

      {showMedici && (
        <Card className="rounded-xl border-emerald-100 shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">Anagrafica medici farmacia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-2 sm:grid-cols-4">
              <Input placeholder="Nome" value={medicoForm.nome} onChange={(e) => setMedicoForm({ ...medicoForm, nome: e.target.value })} />
              <Input placeholder="Email" value={medicoForm.email} onChange={(e) => setMedicoForm({ ...medicoForm, email: e.target.value })} />
              <Input placeholder="Telefono" value={medicoForm.telefono} onChange={(e) => setMedicoForm({ ...medicoForm, telefono: e.target.value })} />
              <Button onClick={saveMedico} className="rounded-xl bg-[#4CAF50] hover:bg-[#43A047]">
                <Plus className="mr-1 h-4 w-4" /> Aggiungi
              </Button>
            </div>
            <ul className="space-y-1 text-sm">
              {medici.map((m) => (
                <li key={m.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                  <span>
                    <strong>{m.nome}</strong>
                    <span className="ml-2 text-xs text-slate-500">{m.email || "—"}</span>
                  </span>
                  <button
                    type="button"
                    className="text-xs text-rose-600"
                    onClick={async () => {
                      await farmDelete(`/farmacia/medici/${m.id}`, token);
                      await load();
                    }}
                  >
                    Elimina
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">
              Clienti <Badge variant="secondary" className="ml-2 rounded-full">{clienti.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[32rem] space-y-1 overflow-y-auto p-2">
            {clienti.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => openCliente(c.id)}
                className={`w-full rounded-xl border px-3 py-2.5 text-left text-sm ${
                  selected?.id === c.id ? "border-emerald-200 bg-emerald-50/70" : "border-transparent hover:bg-slate-50"
                }`}
              >
                <div className="font-medium">
                  {c.cognome} {c.nome}
                </div>
                <div className="text-xs text-slate-500">{c.codice_fiscale || "—"}</div>
              </button>
            ))}
            {clienti.length === 0 && (
              <p className="p-6 text-center text-sm text-slate-400">Nessun cliente diretto</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-sm">
          <CardHeader className="border-b px-4 py-3">
            <CardTitle className="text-sm">{selected ? "Modifica cliente" : "Nuovo cliente"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 p-4 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="Cognome *" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
              <Input placeholder="Nome *" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              <Input placeholder="Codice fiscale" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} />
              <Input placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <select
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={form.medico_id}
                onChange={(e) => setForm({ ...form, medico_id: e.target.value })}
              >
                <option value="">Medico…</option>
                {medici.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs font-semibold text-slate-700">
                  <Pill className="h-3.5 w-3.5" /> Terapie attuali
                </span>
                <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addTerapia}>
                  <Plus className="mr-1 h-3 w-3" /> Farmaco
                </Button>
              </div>
              <div className="space-y-2">
                {terapie.map((t, idx) => (
                  <div key={t.id || idx} className="grid gap-1 rounded-lg border border-slate-100 p-2 sm:grid-cols-3">
                    <Input
                      placeholder="Farmaco"
                      value={t.farmaco || ""}
                      onChange={(e) => {
                        const next = [...terapie];
                        next[idx] = { ...t, farmaco: e.target.value };
                        setTerapie(next);
                      }}
                    />
                    <Input
                      placeholder="Dosaggio"
                      value={t.dosaggio || ""}
                      onChange={(e) => {
                        const next = [...terapie];
                        next[idx] = { ...t, dosaggio: e.target.value };
                        setTerapie(next);
                      }}
                    />
                    <Input
                      placeholder="Frequenza"
                      value={t.frequenza || ""}
                      onChange={(e) => {
                        const next = [...terapie];
                        next[idx] = { ...t, frequenza: e.target.value };
                        setTerapie(next);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={saveCliente} disabled={saving} className="w-full rounded-xl bg-[#4CAF50] hover:bg-[#43A047]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salva
            </Button>

            {selected?.id && (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  disabled={saving}
                  onClick={() => inviaRichiesta(null)}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Genera PDF terapie e crea richiesta
                </Button>
                <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2.5 text-sm font-medium text-emerald-800">
                  <Upload className="h-4 w-4" />
                  Carica PDF richiesta / Carepy
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => inviaRichiesta(e.target.files?.[0])}
                  />
                </label>
                <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700">
                  <Upload className="h-4 w-4" />
                  Carica ricetta ricevuta dal medico
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadRicetta} />
                </label>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
